import { useLayoutEffect, useRef, useState } from "react"
import styled from "styled-components"

import { HalfDoughnutLegendItem } from "./HalfDoughnutLegendItem"
import { HalfDoughnutSummaryItem } from "./styled"

const LegendGroups = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px 0;
`

const LegendGroup = styled.div<{ $flex: number; $divider: boolean }>`
  display: flex;
  justify-content: center;
  flex: ${({ $flex }) => $flex};
  /* Divider between groups — shown only while the groups share a row, so it never
     dangles at a line break (toggled off on wrap via $divider). */
  border-left: ${({ $divider }) => ($divider ? "1px solid #f1f5f9" : "none")};

  /* Divider between items within a group (the last item of each group has none). */
  > *:not(:last-child) {
    border-right: 1px solid #f1f5f9;
  }

  > * {
    padding: 0 16px;
  }
`

interface HalfDoughnutLegendGroupsProps {
  groups: HalfDoughnutSummaryItem[][]
  total?: number
}

export const HalfDoughnutLegendGroups = ({ groups, total }: HalfDoughnutLegendGroupsProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [singleRow, setSingleRow] = useState(true)

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return undefined

    const update = () => {
      const rows = Array.from(container.children) as HTMLElement[]
      setSingleRow(rows.every(row => row.offsetTop === rows[0]?.offsetTop))
    }

    update()
    const observer = new ResizeObserver(update)
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  return (
    <LegendGroups ref={containerRef}>
      {groups.map((group, index) => (
        <LegendGroup
          key={group.map(item => item.label).join("-")}
          $flex={group.length}
          $divider={singleRow && index > 0}
        >
          {group.map(item => (
            <HalfDoughnutLegendItem key={item.label} {...item} total={total} />
          ))}
        </LegendGroup>
      ))}
    </LegendGroups>
  )
}
